import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import type { Room, TwilioError } from 'twilio-video';
import { logger } from '../../../utils/logger';

type VideoNetworkConnectionProps = {
  room: Room;
  hangupHandler: Function;
};

// Video rooms have their own connection lifecycle, independent of Voice registration.
export function VideoNetworkConnection({ room, hangupHandler }: VideoNetworkConnectionProps) {
  const [connection, setConnection] = useState({ room, state: room.state });
  const hangupRef = useRef(hangupHandler);
  useEffect(() => {
    hangupRef.current = hangupHandler;
  }, [hangupHandler]);

  useEffect(() => {
    let handledDisconnect = false;
    const updateState = () => setConnection({ room, state: room.state });
    const logState = (error?: TwilioError) => {
      logger({
        message: '[VideoNetworkConnection]: Room connection changed',
        data: { roomSid: room.sid, state: room.state, code: error?.code, message: error?.message },
      });
    };
    const onReconnecting = (error: TwilioError) => {
      logState(error);
      updateState();
    };
    const onReconnected = () => {
      logState();
      updateState();
    };
    const onDisconnected = (_room: Room, error?: TwilioError) => {
      updateState();
      if (handledDisconnect) return;
      handledDisconnect = true;
      logState(error);
      // Normal hangup already performs cleanup. Avoid re-entering it from disconnect().
      if (error) {
        toast.error('The video call disconnected. Please rejoin the call.');
        hangupRef.current();
      }
    };

    room.on('reconnecting', onReconnecting);
    room.on('reconnected', onReconnected);
    room.on('disconnected', onDisconnected);
    updateState();
    return () => {
      room.off('reconnecting', onReconnecting);
      room.off('reconnected', onReconnected);
      room.off('disconnected', onDisconnected);
    };
  }, [room]);

  const state = connection.room === room ? connection.state : room.state;
  if (state !== 'reconnecting') return null;

  // Signaling can reconnect while media still works. Let the SDK finish its retries.
  return (
    <div
      className="offline-device d-block"
      role="status"
      aria-live="polite"
    >
      <div className="offline-message-wrapper">
        <p>You lost your network connection. Trying to reconnect.</p>
        <div className="spinner-border text-primary mt-2" aria-hidden="true">
          <span className="visually-hidden" />
        </div>
      </div>
    </div>
  );
}
